// apps/cli/src/commands/enterprise/campaigns/list.ts
import type { Command } from 'commander';
import { asPaginatedBody } from '@wport/core';
import { enterpriseGet } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { dim, printJson, printTable, type OutputFormat } from '../../../lib/output';
import { parseFieldsList, pickPaths } from '../../../lib/path-utils';

interface ListFlags {
	page?: number;
	pageSize?: number;
	keyword?: string;
	status?: string;
	fields?: string;
	minimal?: boolean;
}

/** Server QueryCampaignsDto.status: number（0=關閉, 1=開啟）。CLI flag open/closed 映射。 */
const STATUS_MAP: Record<string, number> = { open: 1, closed: 0 };

const MINIMAL_LIST_FIELDS = ['enc_id', 'name', 'status', 'job_count'];

/** 欄位對齊 server CampaignListItemVm。 */
interface CampaignItem {
	enc_id?: string;
	name?: string | null;
	slug?: string | null;
	status?: number;
	pv?: number | null;
	visitors_count?: number | null;
	job_count?: number | null;
	is_all_selected?: boolean;
	landing_url?: string | null;
	[k: string]: unknown;
}

function mapStatusFlag(raw: string | undefined): number | undefined {
	if (raw === undefined) return undefined;
	if (Object.prototype.hasOwnProperty.call(STATUS_MAP, raw)) return STATUS_MAP[raw];
	throw new CliError(`Invalid --status "${raw}". Allowed: ${Object.keys(STATUS_MAP).join(', ')}`, ExitCode.InvalidArgument);
}

export function formatStatus(status: number | undefined): string {
	if (status === 1) return 'open';
	if (status === 0) return 'closed';
	return status === undefined ? '' : String(status);
}

function formatCount(value: number | null | undefined): string {
	return typeof value === 'number' && Number.isFinite(value) ? String(value) : '—';
}

export async function runEnterpriseCampaignsList(
	ctx: { baseUrl: string; locale: string; timeoutMs: number; format: OutputFormat; color: boolean },
	apiKey: string,
	flags: ListFlags
): Promise<void> {
	if (flags.fields && flags.minimal) {
		throw new CliError('Use either --fields or --minimal, not both', ExitCode.InvalidArgument);
	}
	const { body } = await enterpriseGet(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		'/campaigns',
		{
			currentPage: flags.page,
			pageSize: flags.pageSize,
			keyword: flags.keyword,
			status: mapStatusFlag(flags.status),
		}
	);
	const paged = asPaginatedBody<CampaignItem>(body);

	const projection = flags.minimal ? MINIMAL_LIST_FIELDS : flags.fields ? parseFieldsList(flags.fields) : undefined;
	if (projection || ctx.format === 'json') {
		printJson(projection ? { ...paged, data: paged.data.map((row) => pickPaths(row, projection)) } : paged);
		return;
	}

	printTable(
		paged.data,
		[
			{ header: 'ENC_ID', value: (r) => (r.enc_id ?? '').slice(0, 14) },
			{ header: 'NAME', value: (r) => r.name ?? '', maxWidth: 32 },
			{ header: 'STATUS', value: (r) => formatStatus(r.status) },
			{ header: 'JOBS', value: (r) => formatCount(r.job_count) },
			{ header: 'PV', value: (r) => formatCount(r.pv) },
			{ header: 'VISITORS', value: (r) => formatCount(r.visitors_count) },
		],
		ctx.color
	);
	const head = `Showing page ${paged.currentPage}/${paged.totalPages} (${paged.data.length} of ${paged.totalCount} campaigns).`;
	const hint =
		paged.totalPages > paged.currentPage ? `  Next: wport enterprise campaigns list --page ${paged.currentPage + 1}` : '';
	process.stdout.write(dim(head + hint, ctx.color) + '\n');
}

export function registerEnterpriseCampaignsList(parent: Command): void {
	parent
		.command('list')
		.description('List your recruitment campaigns')
		.option('-p, --page <n>', 'page number (server: currentPage, default 1)', (v) => Number(v))
		.option('-s, --page-size <n>', 'items per page (default 10, max 100)', (v) => Number(v))
		.option('-k, --keyword <kw>', 'filter by campaign name keyword')
		.option('--status <state>', 'filter by status: open | closed')
		.option('--fields <list>', 'output selected fields as JSON (comma-separated dotted paths)')
		.option('--minimal', `output only ${MINIMAL_LIST_FIELDS.join(',')} as JSON`)
		.action(async (flags: ListFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runEnterpriseCampaignsList(ctx, key, flags);
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { mapStatusFlag, formatStatus, formatCount };
